/* ============================================================
   Kingston Goods — theme.js
   Drawer + menu controllers, search, accordions, PDP variants.
   ============================================================ */

(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────
  // Drawer controller: open / close + scrim sync
  // ─────────────────────────────────────────────────────────
  // Registry of every drawer so opening one can close the others
  // (prevents e.g. the search modal opening behind the mobile menu).
  const _drawers = [];

  function bindDrawer(drawerSel, scrimSel, openSels, closeSels) {
    const drawer = document.querySelector(drawerSel);
    const scrim = document.querySelector(scrimSel);
    if (!drawer || !scrim) return null;

    function close() {
      drawer.classList.remove('is-open');
      scrim.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
    function open() {
      // Close any other open drawer first so this one is never hidden behind it.
      _drawers.forEach((d) => { if (d.drawer !== drawer) d.close(); });
      drawer.classList.add('is-open');
      scrim.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    openSels.forEach((s) => document.querySelectorAll(s).forEach((el) => el.addEventListener('click', (e) => { e.preventDefault(); open(); })));
    closeSels.forEach((s) => document.querySelectorAll(s).forEach((el) => el.addEventListener('click', (e) => { e.preventDefault(); close(); })));
    scrim.addEventListener('click', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    const api = { open, close, drawer };
    _drawers.push(api);
    return api;
  }

  const cart   = bindDrawer('[data-cart-drawer]',   '[data-cart-scrim]',   ['[data-cart-open]'],   ['[data-cart-close]']);
  const search = bindDrawer('[data-search-drawer]', '[data-search-scrim]', ['[data-search-open]'], ['[data-search-close]']);
  const menu   = bindDrawer('[data-menu-drawer]',   '[data-menu-scrim]',   ['[data-menu-open]'],   ['[data-menu-close]']);

  window.KGDrawers = { cart, search, menu };

  // Search suggestions
  document.querySelectorAll('[data-search-suggest]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const input = document.querySelector('[data-search-input]');
      if (input) { input.value = btn.dataset.searchSuggest; input.focus(); }
    });
  });

  // ─────────────────────────────────────────────────────────
  // PDP — qty + variant picker + add-to-cart
  // ─────────────────────────────────────────────────────────
  const pdp = document.querySelector('[data-product-section]');
  if (pdp) {
    const productData = (() => {
      const tag = pdp.parentElement.querySelector('[data-product-json]');
      try { return tag ? JSON.parse(tag.textContent) : null; } catch (e) { return null; }
    })();

    // Quantity
    const qtyVal = pdp.querySelector('[data-qty-value]');
    const qtyInp = pdp.querySelector('[data-qty-input]');
    pdp.querySelector('[data-qty-inc]')?.addEventListener('click', () => {
      const v = Math.max(1, parseInt(qtyVal.textContent, 10) + 1);
      qtyVal.textContent = v; qtyInp.value = v;
    });
    pdp.querySelector('[data-qty-dec]')?.addEventListener('click', () => {
      const v = Math.max(1, parseInt(qtyVal.textContent, 10) - 1);
      qtyVal.textContent = v; qtyInp.value = v;
    });

    // Variant selection
    function getSelectedOptions() {
      const opts = [];
      pdp.querySelectorAll('[data-option-index]').forEach((group) => {
        const i = parseInt(group.dataset.optionIndex, 10);
        const onBtn = group.querySelector('[data-option-button].is-on');
        opts[i] = onBtn ? onBtn.dataset.value : null;
      });
      return opts;
    }
    function findVariant(opts) {
      if (!productData) return null;
      return productData.variants.find((v) =>
        v.options.every((val, i) => val === opts[i])
      );
    }
    function updateForVariant(variant) {
      if (!variant) return;
      pdp.querySelector('[data-variant-id]')?.setAttribute('value', variant.id);
      const priceFmt = formatMoney(variant.price);
      const cmpFmt = variant.compare_at_price && variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : null;
      const priceEl = pdp.querySelector('[data-product-price]');
      if (priceEl) priceEl.innerHTML = (cmpFmt ? `<s>${cmpFmt}</s> ` : '') + priceFmt;
      const btnPrice = pdp.querySelector('[data-product-price-btn]');
      if (btnPrice) btnPrice.textContent = priceFmt;
      const submit = pdp.querySelector('[data-add-to-cart]');
      if (submit) {
        submit.disabled = !variant.available;
        if (!variant.available) {
          submit.textContent = (window.theme.strings.soldOut || 'Sold out').toUpperCase();
        } else {
          // Rebuild the button label + price span so it recovers after a sold-out variant.
          const atcLabel = (window.theme.strings.addToCart || 'Add to cart').toUpperCase();
          submit.innerHTML = atcLabel + ' — <span data-product-price-btn>' + priceFmt + '</span>';
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    pdp.querySelectorAll('[data-option-button]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const group = btn.closest('[data-option-index]');
        group.querySelectorAll('[data-option-button]').forEach((b) => b.classList.remove('is-on'));
        btn.classList.add('is-on');
        const lbl = group.querySelector('[data-option-value]');
        if (lbl) lbl.textContent = btn.dataset.value;
        updateForVariant(findVariant(getSelectedOptions()));
      });
    });

    // Accordions
    pdp.querySelectorAll('[data-accordion]').forEach((acc) => {
      const toggle = acc.querySelector('[data-accordion-toggle]');
      const icon = acc.querySelector('[data-accordion-icon]');
      toggle.addEventListener('click', () => {
        acc.classList.toggle('is-open');
        if (icon) icon.innerHTML = acc.classList.contains('is-open')
          ? '<svg class="kgr-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25"><path d="M5 12h14"/></svg>'
          : '<svg class="kgr-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25"><path d="M12 5v14 M5 12h14"/></svg>';
      });
    });
  }

  // ─────────────────────────────────────────────────────────
  // PLP — sort dropdown + filter group toggles + auto-submit
  // ─────────────────────────────────────────────────────────
  document.querySelectorAll('[data-filter-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const group = btn.closest('[data-filter-group]');
      const body = group.querySelector('[data-filter-body]');
      const icon = group.querySelector('[data-filter-icon]');
      const isOpen = body.style.display !== 'none';
      body.style.display = isOpen ? 'none' : '';
      if (icon) icon.innerHTML = isOpen
        ? '<svg class="kgr-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25"><path d="M12 5v14 M5 12h14"/></svg>'
        : '<svg class="kgr-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25"><path d="M5 12h14"/></svg>';
    });
  });
  const filterForm = document.querySelector('[data-collection-filter]');
  if (filterForm) {
    filterForm.addEventListener('change', () => {
      const formData = new FormData(filterForm);
      const params = new URLSearchParams();
      formData.forEach((v, k) => { if (v) params.append(k, v); });
      const url = new URL(window.location.href);
      url.search = '?' + params.toString();
      window.location = url.toString();
    });
  }
  const sortSelect = document.querySelector('[data-sort-select]');
  if (sortSelect) {
    document.querySelector('[data-collection-sort] .kgr-plp__sort')?.addEventListener('click', () => sortSelect.click());
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location = url.toString();
    });
  }

  // ─────────────────────────────────────────────────────────
  // Helpers — money formatting (cents → $X.XX)
  // ─────────────────────────────────────────────────────────
  function formatMoney(cents, format) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10);
    if (isNaN(cents)) cents = 0;
    // Mirror the shop's money format (read from the PDP root) so JS-rendered
    // prices always match the server-rendered ones — no decimal fluctuation.
    var fmtEl = document.querySelector('[data-money-format]');
    var fmt = format || (fmtEl && fmtEl.getAttribute('data-money-format')) || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function withDelimiters(number, precision, thousands, decimal) {
      precision = (precision === undefined) ? 2 : precision;
      thousands = (thousands === undefined) ? ',' : thousands;
      decimal = (decimal === undefined) ? '.' : decimal;
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var frac = parts[1] ? (decimal + parts[1]) : '';
      return whole + frac;
    }
    var match = fmt.match(placeholder);
    var token = match ? match[1] : 'amount';
    var value;
    switch (token) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ','); break;
      default: value = withDelimiters(cents, 2); break; // 'amount'
    }
    return fmt.replace(placeholder, value);
  }
  window.formatMoney = formatMoney;
})();
/* === Kingston Goods — PDP gallery (main + thumbs + hover zoom) & size-guide reposition === */
(function () {
  function initPdpGallery() {
    var root = document.querySelector('.kgr-pdp__gallery');
    if (!root || root.dataset.kgrPdpInit) return;
    root.dataset.kgrPdpInit = '1';
 
    var cells = Array.prototype.slice.call(root.querySelectorAll('div'));
    var imgs = cells.map(function (c) {
      var m = (c.style.backgroundImage || '').match(/url\(["']?([^"')]+)["']?\)/);
      return m ? m[1] : null;
    }).filter(Boolean);
    if (!imgs.length) return;
 
    root.innerHTML = '';
    root.classList.add('kgr-pdp__gallery--stack');
 
    var stage = document.createElement('div');
    stage.className = 'kgr-pdp__stage';
    var mainImg = document.createElement('img');
    mainImg.className = 'kgr-pdp__main-img';
    mainImg.src = imgs[0];
    mainImg.alt = '';
    stage.appendChild(mainImg);
    root.appendChild(stage);
 
    if (imgs.length > 1) {
      var thumbs = document.createElement('div');
      thumbs.className = 'kgr-pdp__thumbs';
      imgs.forEach(function (src, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kgr-pdp__thumb' + (i === 0 ? ' is-on' : '');
        btn.style.backgroundImage = "url('" + src + "')";
        btn.setAttribute('aria-label', 'View image ' + (i + 1));
        btn.addEventListener('click', function () {
          mainImg.src = src;
          thumbs.querySelectorAll('.kgr-pdp__thumb').forEach(function (t) { t.classList.remove('is-on'); });
          btn.classList.add('is-on');
        });
        thumbs.appendChild(btn);
      });
      root.appendChild(thumbs);
    }
 
    stage.addEventListener('mousemove', function (e) {
      var r = stage.getBoundingClientRect();
      var x = ((e.clientX - r.left) / r.width) * 100;
      var y = ((e.clientY - r.top) / r.height) * 100;
      mainImg.style.transformOrigin = x + '% ' + y + '%';
    });
    stage.addEventListener('mouseenter', function () { mainImg.style.transform = 'scale(1.6)'; });
    stage.addEventListener('mouseleave', function () {
      mainImg.style.transform = 'scale(1)';
      mainImg.style.transformOrigin = '50% 50%';
    });

    // Mobile only: touch-and-drag magnifier. Touch the main image to zoom, and
    // the zoom follows the finger as it moves — mirroring the old theme.
    var kgrMqTouchZoom = window.matchMedia('(max-width: 900px)');
    function kgrTouchPoint(e) {
      var t = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]);
      if (!t) return;
      var r = stage.getBoundingClientRect();
      var x = Math.max(0, Math.min(100, ((t.clientX - r.left) / r.width) * 100));
      var y = Math.max(0, Math.min(100, ((t.clientY - r.top) / r.height) * 100));
      mainImg.style.transformOrigin = x + '% ' + y + '%';
    }
    function kgrTouchZoomStart(e) {
      if (!kgrMqTouchZoom.matches) return;
      kgrTouchPoint(e);
      mainImg.style.transform = 'scale(2.2)';
      e.preventDefault(); // lock page scroll while zooming
    }
    function kgrTouchZoomMove(e) {
      if (!kgrMqTouchZoom.matches) return;
      kgrTouchPoint(e);
      e.preventDefault();
    }
    function kgrTouchZoomEnd() {
      mainImg.style.transform = 'scale(1)';
      mainImg.style.transformOrigin = '50% 50%';
    }
    stage.addEventListener('touchstart', kgrTouchZoomStart, { passive: false });
    stage.addEventListener('touchmove', kgrTouchZoomMove, { passive: false });
    stage.addEventListener('touchend', kgrTouchZoomEnd);
    stage.addEventListener('touchcancel', kgrTouchZoomEnd);
  }
 
  function movePdpSizeGuide() {
    var info = document.querySelector('.kgr-pdp__info');
    if (!info || info.dataset.kgrSizeGuideMoved) return;
    var guide = info.querySelector('a[href="#size-guide"]');
    if (!guide) return;
    var wrap = guide.closest('[data-option-index]');
    if (!wrap || !wrap.parentNode) return;
    info.dataset.kgrSizeGuideMoved = '1';
    guide.parentNode.removeChild(guide);
    guide.classList.add('kgr-pdp__size-guide-link');
    wrap.parentNode.insertBefore(guide, wrap.parentNode.firstChild);
  }
 
  function init() { initPdpGallery(); movePdpSizeGuide(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
})();
/* === Kingston Goods — PDP gallery (main + thumbs + hover zoom) & size-guide reposition === */
(function () {
  function initPdpGallery() {
    var root = document.querySelector('.kgr-pdp__gallery');
    if (!root || root.dataset.kgrPdpInit) return;
    root.dataset.kgrPdpInit = '1';
 
    var cells = Array.prototype.slice.call(root.querySelectorAll('div'));
    var imgs = cells.map(function (c) {
      var m = (c.style.backgroundImage || '').match(/url\(["']?([^"')]+)["']?\)/);
      return m ? m[1] : null;
    }).filter(Boolean);
    if (!imgs.length) return;
 
    root.innerHTML = '';
    root.classList.add('kgr-pdp__gallery--stack');
 
    var stage = document.createElement('div');
    stage.className = 'kgr-pdp__stage';
    var mainImg = document.createElement('img');
    mainImg.className = 'kgr-pdp__main-img';
    mainImg.src = imgs[0];
    mainImg.alt = '';
    stage.appendChild(mainImg);
    root.appendChild(stage);
 
    if (imgs.length > 1) {
      var thumbs = document.createElement('div');
      thumbs.className = 'kgr-pdp__thumbs';
      imgs.forEach(function (src, i) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kgr-pdp__thumb' + (i === 0 ? ' is-on' : '');
        btn.style.backgroundImage = "url('" + src + "')";
        btn.setAttribute('aria-label', 'View image ' + (i + 1));
        btn.addEventListener('click', function () {
          mainImg.src = src;
          thumbs.querySelectorAll('.kgr-pdp__thumb').forEach(function (t) { t.classList.remove('is-on'); });
          btn.classList.add('is-on');
        });
        thumbs.appendChild(btn);
      });
      root.appendChild(thumbs);
    }
 
    stage.addEventListener('mousemove', function (e) {
      var r = stage.getBoundingClientRect();
      var x = ((e.clientX - r.left) / r.width) * 100;
      var y = ((e.clientY - r.top) / r.height) * 100;
      mainImg.style.transformOrigin = x + '% ' + y + '%';
    });
    stage.addEventListener('mouseenter', function () { mainImg.style.transform = 'scale(1.6)'; });
    stage.addEventListener('mouseleave', function () {
      mainImg.style.transform = 'scale(1)';
      mainImg.style.transformOrigin = '50% 50%';
    });

    // Mobile only: touch-and-drag magnifier. Touch the main image to zoom, and
    // the zoom follows the finger as it moves — mirroring the old theme.
    var kgrMqTouchZoom = window.matchMedia('(max-width: 900px)');
    function kgrTouchPoint(e) {
      var t = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]);
      if (!t) return;
      var r = stage.getBoundingClientRect();
      var x = Math.max(0, Math.min(100, ((t.clientX - r.left) / r.width) * 100));
      var y = Math.max(0, Math.min(100, ((t.clientY - r.top) / r.height) * 100));
      mainImg.style.transformOrigin = x + '% ' + y + '%';
    }
    function kgrTouchZoomStart(e) {
      if (!kgrMqTouchZoom.matches) return;
      kgrTouchPoint(e);
      mainImg.style.transform = 'scale(2.2)';
      e.preventDefault(); // lock page scroll while zooming
    }
    function kgrTouchZoomMove(e) {
      if (!kgrMqTouchZoom.matches) return;
      kgrTouchPoint(e);
      e.preventDefault();
    }
    function kgrTouchZoomEnd() {
      mainImg.style.transform = 'scale(1)';
      mainImg.style.transformOrigin = '50% 50%';
    }
    stage.addEventListener('touchstart', kgrTouchZoomStart, { passive: false });
    stage.addEventListener('touchmove', kgrTouchZoomMove, { passive: false });
    stage.addEventListener('touchend', kgrTouchZoomEnd);
    stage.addEventListener('touchcancel', kgrTouchZoomEnd);
  }
 
  function movePdpSizeGuide() {
    var info = document.querySelector('.kgr-pdp__info');
    if (!info || info.dataset.kgrSizeGuideMoved) return;
    var guide = info.querySelector('a[href="#size-guide"]');
    if (!guide) return;
    var wrap = guide.closest('[data-option-index]');
    if (!wrap || !wrap.parentNode) return;
    info.dataset.kgrSizeGuideMoved = '1';
    guide.parentNode.removeChild(guide);
    guide.classList.add('kgr-pdp__size-guide-link');
    wrap.parentNode.insertBefore(guide, wrap.parentNode.firstChild);
  }
 
  function init() { initPdpGallery(); movePdpSizeGuide(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
})();